import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useQuery } from "@tanstack/react-query";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import Modal from "../../components/ui-components/Modal";
import Badge from "../../components/ui-components/Badge";
import { Dropdown } from "../../components/form-components/Dropdown";
import { primaryButtonCls, secondaryButtonCls } from "../../components/ui-components/buttonStyles.js";
import { toast } from "../../components/ui-components/Toast";
import { useHasCapability } from "../../authz/useCapabilities";
import { CAPS } from "../../authz/capabilities";
import { listUsers } from "../users/api";
import { useAccessReview } from "../access-review/api";
import {
  useGrantPlatformRoleMutation,
  useRevokePlatformRoleMutation,
  usePlatformGrantRequests,
  useApprovePlatformGrantRequestMutation,
  useRejectPlatformGrantRequestMutation,
} from "./api";

// The break-glass emergency role — granted only temporarily (≤24h), immediately
// (bypassing the tenant approval gate), and with a critical audit trail. Keep
// this code in sync with the backend authz.SystemRoleTypes emergency_access.
const EMERGENCY_ACCESS_CODE = "emergency_access";

// Short "expires 12 Aug" badge label for a temporary grant. Returns null for
// a permanent grant (expiresAt nil) or an unparseable timestamp, so the
// caller falls back to the "Permanent" badge.
function expiryLabel(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return `Expires ${d.toLocaleDateString(undefined, { day: "numeric", month: "short" })}`;
}

// Maps backend error codes (cmd/api/platform_grant_handlers.go) to messages a
// tenant admin can act on. PLATFORM_APPROVAL_REQUIRED is the one a caller
// most needs to distinguish — the grant wasn't rejected for being wrong, the
// tenant's policy just requires a separate approval step this endpoint
// doesn't (yet) drive.
function friendlyGrantError(err) {
  const code = err?.serverBody?.error?.code;
  const serverMessage = err?.serverBody?.error?.message;
  if (code === "PLATFORM_APPROVAL_REQUIRED") {
    return serverMessage || "This tenant requires approval for platform-role assignments.";
  }
  if (code === "NOT_PLATFORM_ROLE") {
    return serverMessage || "This role can't be granted from here.";
  }
  if (code === "EMERGENCY_ACCESS_REQUIRES_EXPIRY") {
    return serverMessage || "Emergency access must be time-boxed — choose an expiry.";
  }
  if (code === "EMERGENCY_ACCESS_WINDOW_TOO_LONG") {
    return serverMessage || "Emergency access can last at most 24 hours.";
  }
  return serverMessage || err?.message || "Failed to grant platform access";
}

// Maps the approve/reject decision error codes to admin-facing messages —
// self-approval (hard SoD) is the one an approver most needs spelled out.
function friendlyDecisionError(err) {
  const code = err?.serverBody?.error?.code;
  const serverMessage = err?.serverBody?.error?.message;
  if (code === "SELF_APPROVAL_FORBIDDEN") {
    return serverMessage || "You can't approve or reject your own request.";
  }
  if (code === "REQUEST_NOT_PENDING") {
    return serverMessage || "This request has already been decided.";
  }
  return serverMessage || err?.message || "Failed to decide the request";
}

// PlatformAssignmentsModal — the Platform tab's "Manage assignments" dialog
// (Sub-project E4, Task 6). `role` is the platform role being managed (its
// `code`/`name`); null keeps the modal closed. Two sections:
//
// - Current holders: every access-review platformHolders entry for this
//   role's code — permanent (no expiresAt) vs temporary (an expiry badge),
//   plus the reason it was granted. This reads GET /access-review, which is
//   server-gated on the INDEPENDENT `access.review` capability (not
//   roles.assign) — a roles.assign-only admin can open this dialog (the
//   Platform tab itself only requires roles.assign) but would 403 on that
//   request, so the query only fires when the viewer actually holds
//   access.review, and the section degrades to an explanatory note
//   otherwise rather than a misleading empty/stuck-loading list.
// - Grant access: a user picker + Permanent/Temporary(until date) + a
//   required reason, gated on roles.assign — independent of the holder
//   list above, so it still works for a roles.assign-only admin. A 409
//   PLATFORM_APPROVAL_REQUIRED or 400 NOT_PLATFORM_ROLE surfaces via toast
//   rather than a silent failure.
export default function PlatformAssignmentsModal({ role, onClose }) {
  const canManage = useHasCapability(CAPS.ROLES_ASSIGN);
  const canViewHolders = useHasCapability(CAPS.ACCESS_REVIEW);
  const isOpen = !!role;

  const [selectedUser, setSelectedUser] = useState(null);
  const [duration, setDuration] = useState("permanent");
  const [expiresOn, setExpiresOn] = useState("");
  const [reason, setReason] = useState("");
  const [revokeCandidate, setRevokeCandidate] = useState(null);

  const usersQ = useQuery(["users-picker-for-platform-grant"], () => listUsers({ limit: 200 }), {
    enabled: isOpen,
    staleTime: 5 * 60 * 1000,
  });
  const userOptions = useMemo(() => {
    const raw = usersQ.data?.users?.items || [];
    return raw.map((u) => ({
      value: u.id ?? u.ID,
      label: [u.first_name, u.last_name].filter(Boolean).join(" ") || u.email || String(u.id ?? u.ID ?? ""),
      subtitle: u.email || undefined,
    }));
  }, [usersQ.data]);

  // Only fires while the dialog is open AND the viewer holds access.review —
  // never on every Roles & Access tab visit (the modal is always mounted by
  // RolesCatalogPage), and never for a viewer the endpoint would 403.
  const reviewQ = useAccessReview({ enabled: isOpen && canViewHolders });
  const holders = useMemo(() => {
    const all = reviewQ.data?.platformHolders || [];
    return role ? all.filter((h) => h.roleCode === role.code) : [];
  }, [reviewQ.data, role]);

  const resetForm = () => {
    setSelectedUser(null);
    setDuration("permanent");
    setExpiresOn("");
    setReason("");
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const isEmergency = role?.code === EMERGENCY_ACCESS_CODE;

  // Pending platform-grant requests for THIS role (the approver worklist).
  // Only fires while the dialog is open and the viewer can manage roles.
  const requestsQ = usePlatformGrantRequests({ enabled: isOpen && canManage });
  const pendingRequests = useMemo(() => {
    const all = requestsQ.data || [];
    return role ? all.filter((req) => req.roleCode === role.code) : [];
  }, [requestsQ.data, role]);

  const grantMutation = useGrantPlatformRoleMutation({
    onSuccess: (data) => {
      // The grant may be immediate, or (when the tenant requires approval and
      // the role isn't emergency_access) create a pending request instead.
      if (data?.status === "pending_approval") {
        toast.success("Request submitted for approval");
      } else {
        toast.success("Platform role granted");
      }
      resetForm();
    },
    onError: (err) => {
      toast.error(friendlyGrantError(err));
    },
  });

  const approveMutation = useApprovePlatformGrantRequestMutation({
    onSuccess: () => toast.success("Request approved — access granted"),
    onError: (err) => toast.error(friendlyDecisionError(err)),
  });
  const rejectMutation = useRejectPlatformGrantRequestMutation({
    onSuccess: () => toast.success("Request rejected"),
    onError: (err) => toast.error(friendlyDecisionError(err)),
  });

  const revokeMutation = useRevokePlatformRoleMutation({
    onSuccess: () => {
      toast.success("Platform role revoked");
      setRevokeCandidate(null);
    },
    onError: (err) => {
      setRevokeCandidate(null);
      toast.error(err?.serverBody?.error?.message || err?.message || "Failed to revoke platform access");
    },
  });

  if (!role) return null;

  const handleGrant = () => {
    if (!selectedUser) {
      toast.error("Choose a user to grant this role to");
      return;
    }
    if (!reason.trim()) {
      toast.error("A reason is required");
      return;
    }
    // Emergency access is always time-boxed — the backend rejects a permanent
    // emergency grant, so the form requires an expiry regardless of the radio.
    const effectiveDuration = isEmergency ? "temporary" : duration;
    if (effectiveDuration === "temporary" && !expiresOn) {
      toast.error(isEmergency ? "Emergency access must have an expiry" : "Choose an expiry date for a temporary grant");
      return;
    }
    const body = { role_code: role.code, reason: reason.trim() };
    if (effectiveDuration === "temporary") {
      // Emergency grants must be within 24h; the backend enforces the cap, but
      // default the picker's usefulness by capping the client-side hint too.
      body.expires_at = new Date(`${expiresOn}T23:59:59.999Z`).toISOString();
    }
    grantMutation.mutate({ userId: selectedUser.value, body });
  };

  return (
    <>
      <Modal
        show={!!role}
        close={handleClose}
        title={`Manage assignments — ${role.name}`}
        icon={icon({ name: "user-shield", style: "solid" })}
        cancelButton=""
        confirmButton=""
        size="lg"
      >
        <div className="space-y-5">
          <div>
            <div className="text-[10.5px] font-bold tracking-[0.08em] uppercase text-text-3 mb-2">
              Current holders
            </div>
            {!canViewHolders ? (
              <div className="text-sm text-text-2 italic" data-testid="platform-holders-no-access">
                Viewing current platform holders requires the Access review capability.
              </div>
            ) : reviewQ.isLoading ? (
              <div className="text-sm text-text-2">Loading…</div>
            ) : holders.length === 0 ? (
              <div className="text-sm text-text-2 italic">No one currently holds this role.</div>
            ) : (
              <ul className="divide-y divide-border list-none m-0 p-0" data-testid="platform-holders-list">
                {holders.map((h) => {
                  const expiry = expiryLabel(h.expiresAt);
                  return (
                    <li key={`${h.userId}-${h.roleCode}`} className="flex items-center justify-between gap-3 py-2">
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-text truncate">{h.email}</div>
                        {h.grantReason && <div className="text-xs text-text-2 truncate">{h.grantReason}</div>}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Badge status={expiry ? "warning" : "neutral"} label={expiry || "Permanent"} size="sm" />
                        {canManage && (
                          <button
                            type="button"
                            onClick={() => setRevokeCandidate(h)}
                            className="text-xs font-medium text-danger hover:opacity-80 cursor-pointer border-0 bg-transparent box-border p-0"
                          >
                            Revoke
                          </button>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {canManage && pendingRequests.length > 0 && (
            <div className="pt-4 border-t border-border">
              <div className="text-[10.5px] font-bold tracking-[0.08em] uppercase text-text-3 mb-2">
                Pending approval requests
              </div>
              <ul className="divide-y divide-border list-none m-0 p-0" data-testid="platform-pending-requests">
                {pendingRequests.map((req) => {
                  const until = expiryLabel(req.expiresAt);
                  const deciding = approveMutation.isPending || rejectMutation.isPending;
                  return (
                    <li key={req.id} className="flex items-start justify-between gap-3 py-2">
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-text truncate">
                          {req.targetEmail || `User #${req.targetUserId}`}
                        </div>
                        <div className="text-xs text-text-2 truncate">
                          {req.reason}
                          {req.requestedByEmail ? ` · requested by ${req.requestedByEmail}` : ""}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Badge status={until ? "warning" : "neutral"} label={until || "Permanent"} size="sm" />
                        <button
                          type="button"
                          disabled={deciding}
                          onClick={() => approveMutation.mutate({ id: req.id, body: {} })}
                          className="text-xs font-medium text-primary hover:text-primary-hover cursor-pointer border-0 bg-transparent box-border p-0 disabled:opacity-50"
                        >
                          Approve
                        </button>
                        <button
                          type="button"
                          disabled={deciding}
                          onClick={() => rejectMutation.mutate({ id: req.id, body: {} })}
                          className="text-xs font-medium text-danger hover:opacity-80 cursor-pointer border-0 bg-transparent box-border p-0 disabled:opacity-50"
                        >
                          Reject
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {canManage && (
            <div className="pt-4 border-t border-border">
              <div className="text-[10.5px] font-bold tracking-[0.08em] uppercase text-text-3 mb-2">
                Grant access
              </div>
              {isEmergency && (
                <div
                  data-testid="emergency-break-glass-warning"
                  className="mb-3 flex items-start gap-2 rounded-ctrl border border-solid border-warning bg-warning-soft px-3 py-2 text-xs text-text-2 box-border"
                >
                  <FontAwesomeIcon
                    icon={icon({ name: "triangle-exclamation", style: "solid" })}
                    className="mt-0.5 text-warning text-[12px]"
                  />
                  <span>
                    <span className="font-semibold text-text">Break-glass access.</span> Emergency access is
                    granted immediately, time-boxed (at most 24 hours), and recorded to the audit trail as a
                    critical event. Use only for incident response.
                  </span>
                </div>
              )}
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-text-2 mb-1.5" htmlFor="platform-grant-user">
                    User
                  </label>
                  <Dropdown
                    inputId="platform-grant-user"
                    options={userOptions}
                    selectedValue={selectedUser}
                    setSelectedValue={setSelectedUser}
                    fullWidth
                    twoLine
                    menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
                  />
                </div>

                <div role="radiogroup" aria-label="Duration" className="flex flex-wrap items-center gap-4">
                  {!isEmergency && (
                    <label className="flex items-center gap-1.5 text-sm text-text cursor-pointer">
                      <input
                        type="radio"
                        name="platform-grant-duration"
                        value="permanent"
                        checked={duration === "permanent"}
                        onChange={() => setDuration("permanent")}
                      />
                      Permanent
                    </label>
                  )}
                  <label className="flex items-center gap-1.5 text-sm text-text cursor-pointer">
                    <input
                      type="radio"
                      name="platform-grant-duration"
                      value="temporary"
                      checked={isEmergency || duration === "temporary"}
                      onChange={() => setDuration("temporary")}
                      disabled={isEmergency}
                    />
                    {isEmergency ? "Temporary (required) — until" : "Temporary — until"}
                  </label>
                  {(isEmergency || duration === "temporary") && (
                    <input
                      type="date"
                      aria-label="Expires on"
                      value={expiresOn}
                      onChange={(e) => setExpiresOn(e.target.value)}
                      className="border border-solid border-border rounded-ctrl px-2 py-1 text-sm box-border"
                    />
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-2 mb-1.5" htmlFor="platform-grant-reason">
                    Reason (required)
                  </label>
                  <input
                    id="platform-grant-reason"
                    type="text"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="Why does this user need this access?"
                    // `w-full` is a no-op here — this theme's tailwind.config.js
                    // overrides theme.width with a tiny custom scale, so the
                    // full-width intent has to be set inline instead (see
                    // reference_tailwind-width-height-noop.md).
                    style={{ width: "100%" }}
                    className="border border-solid border-border rounded-ctrl px-3 py-1.5 text-sm box-border focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                </div>

                <div className="flex items-center gap-2.5 pt-1">
                  <button
                    type="button"
                    className={primaryButtonCls}
                    onClick={handleGrant}
                    disabled={grantMutation.isPending}
                  >
                    {grantMutation.isPending ? "Granting…" : "Grant"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2.5 mt-5 pt-4 border-t border-border">
          <button type="button" className={secondaryButtonCls} onClick={handleClose}>
            Close
          </button>
        </div>
      </Modal>

      <Modal
        show={!!revokeCandidate}
        close={() => setRevokeCandidate(null)}
        title="Revoke platform access?"
        description={revokeCandidate ? `${revokeCandidate.email} will immediately lose the ${role.name} role.` : ""}
        confirmButton="Revoke"
        cancelButton="Cancel"
        alertIcon
        confirmAction={() =>
          revokeMutation.mutate({ userId: revokeCandidate.userId, roleCode: revokeCandidate.roleCode })
        }
      />
    </>
  );
}

PlatformAssignmentsModal.propTypes = {
  role: PropTypes.shape({ code: PropTypes.string, name: PropTypes.string }),
  onClose: PropTypes.func.isRequired,
};
